import { Container, Section, Stack } from '@grubhubprod/gh-cookbook-react/dist';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { subscribe } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import { Address } from '@grubhubprod/order-taking-client-sdk/src/sdk/types/geocode';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AccountCorporateSection } from 'src/components/Account/AccountCorporateSection';
import { BalanceInvoicesSection } from 'src/components/Account/BalanceInvoicesSection/BalanceInvoicesSection';
import { AccountPaymentSection } from 'src/components/Account/AccountPaymentSection/AccountPaymentSection';
import { EditCorpModal } from 'src/components/Account/EditCorpModal';
import { AddressConfirmationModal } from 'src/components/AddressConfirmationModal/AddressConfirmationModal';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { CongratulatoryModal } from 'src/components/CongratulatoryModal/CongratulatoryModal';
import {
  pushError,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';
import { sdk } from 'src/utilities/sdk';
import { IDiner } from 'src/utilities/useSearchHooks';
import { useGetQueryParams } from 'src/utilities/useGetQueryParams';
import { PageHeader } from 'src/components/selfService/layout/PageHeader/PageHeader';
import { PageTitle } from 'src/components/selfService/layout/PageTitle/PageTitle';
import { BalanceProvider } from 'src/components/selfService/common/Balance/context/BalanceProvider';
import { useBalanceAccountInfo } from 'src/components/selfService/common/Balance/hooks/useBalanceAccountInfo';
import { NetTermsPromotionSection } from 'src/components/selfService/common/Balance/sections/NetTermsPromotionSection/NetTermsPromotionSection';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FeatureFlag } from 'src/types/FeatureFlag';

export interface ICorpLocation {
  address: string;
  city: string;
  floor: string;
  latitude: number | string;
  longitude: number | string;
  phone: string;
  state: string;
  zip: string;
}

export interface ICorp {
  name: string;
  diner_id?: string;
  business_email?: string;
  location: ICorpLocation;
  adminInfo?: Partial<IDiner>;
  utm_details?: string;
}

export default function AccountPage(): JSX.Element {
  const { corpid: corpId } = useParams<{ corpid: string }>();
  const firstTime = useGetQueryParams('firstTime');

  const {
    enterpriseData: { isInternalAdminUser },
  } = useAuth();

  const [corp, setCorp] = useState<ICorp>(null);
  const isFirstTimeVisit = Boolean(firstTime);
  const { isBalanceAccount } = useBalanceAccountInfo();
  const [showCongratulatoryModal, setShowCongratulatoryModal] = useState(
    isFirstTimeVisit && isBalanceAccount
  );
  const [showEditCorpModal, setShowEditCorpModal] = useState({
    showModal: false,
    corpInfo: null,
  });
  const [addressConfirmationModal, setAddressConfirmationModal] = useState({
    showModal: false,
    suggestedGeoAddresses: [],
    currentAddressString: '',
  });

  const { isLoading: isBalanceUiLoading, value: isBalanceUiEnabled } =
    useFeatureFlag(FeatureFlag.ENABLE_BALANCE_UI);

  const { sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    if (!isFirstTimeVisit) {
      sendPageViewEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstTime, isFirstTimeVisit]);

  useEffect(() => {
    if (corpId) {
      getCorp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  useEffect(() => {
    const subscription = subscribe(
      'showAddressConfirmationModal',
      ({
        corpInfo,
        suggestedGeoAddresses,
      }: {
        corpInfo: ICorp;
        suggestedGeoAddresses: Address[];
      }) => {
        showAddressConfirmationModal(corpInfo, suggestedGeoAddresses);
        setShowEditCorpModal({ showModal: false, corpInfo });
      }
    );

    return () => {
      subscription();
    };
  }, []);

  function sendPageViewEvent() {
    sendTealiumPageView({
      pageGroup: 'corporate self service - account management',
      pageName: 'Account Details',
    });
  }

  function closeCongratulatoryModal() {
    setShowCongratulatoryModal(false);
    // send page view event once modal is closed
    sendPageViewEvent();
  }

  async function getCorp() {
    try {
      const response = await sdk.http.get(`/corp-admin/corp/self/${corpId}`);

      const corp = get(response, 'object') as ICorp;

      if (corp) {
        // Get admin identity
        const { first_name, last_name, email } = await sdk.http.get(
          `/corp-admin/${corpId}/diners/${corp.diner_id}`
        );
        setCorp({ ...corp, adminInfo: { first_name, last_name, email } });
      }
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  async function onEditCorpSuccess() {
    await getCorp();
    setShowEditCorpModal({ showModal: false, corpInfo: corp });
    pushSuccess("Your company's details have been updated");
  }

  async function onEditCorpError() {
    pushError(
      "There was a problem updating your company's details. Try again."
    );
  }

  function onAddressConfirmationSuccess(address: Address) {
    setAddressConfirmationModal({
      showModal: false,
      currentAddressString: '',
      suggestedGeoAddresses: null,
    });

    const {
      address_region: state,
      address_locality: city,
      address_sublocality: city2,
      street_address1: address1,
      street_address2: address2,
      postal_code: zip,
      latitude,
      longitude,
    } = address;

    const { location } = showEditCorpModal.corpInfo;

    const corpInfo: ICorp = {
      ...showEditCorpModal.corpInfo,
      location: {
        phone: location.phone,
        state,
        city: city || city2,
        address: address1,
        floor: address2 || location.floor,
        zip,
        latitude,
        longitude,
      },
    };

    setShowEditCorpModal({ corpInfo, showModal: true });
  }

  function onAddressConfirmationCancel() {
    setShowEditCorpModal({ ...showEditCorpModal, showModal: true });
    setAddressConfirmationModal({
      showModal: false,
      currentAddressString: '',
      suggestedGeoAddresses: null,
    });
  }

  function showAddressConfirmationModal(
    corpInfo: ICorp,
    suggestedGeoAddresses: Address[]
  ) {
    const { address, city, state, zip } = corpInfo.location;
    setAddressConfirmationModal({
      showModal: true,
      suggestedGeoAddresses,
      currentAddressString: `${address}, ${city}, ${state}, ${zip}`,
    });
  }

  const pageHeaderY = isBalanceUiLoading || !isBalanceUiEnabled ? 'sm' : 'md';

  return (
    <>
      <Container variant="default" className="accountPage">
        <Section inset="md">
          <PageHeader y={pageHeaderY}>
            <PageTitle title="Your account" />
          </PageHeader>
          <BalanceProvider>
            {isBalanceAccount && <NetTermsPromotionSection />}
            {corp && (
              <Stack y="md">
                <AccountCorporateSection
                  corp={corp}
                  onEditClick={() =>
                    setShowEditCorpModal({
                      showModal: true,
                      corpInfo: corp,
                    })
                  }
                />
              </Stack>
            )}

            {isBalanceAccount && (
              <Stack y="md">
                <BalanceInvoicesSection />
              </Stack>
            )}

            <AccountPaymentSection
              corpId={corpId as string}
              isInternalAdminUser={isInternalAdminUser}
            />
          </BalanceProvider>
        </Section>
        {showEditCorpModal.showModal && (
          <EditCorpModal
            corpInfo={showEditCorpModal.corpInfo}
            onError={onEditCorpError}
            corpId={corpId as string}
            onCloseModal={() =>
              setShowEditCorpModal({ showModal: false, corpInfo: null })
            }
            onSuccess={onEditCorpSuccess}
          />
        )}

        {addressConfirmationModal.showModal && (
          <AddressConfirmationModal
            isAccount
            currentAddressString={addressConfirmationModal.currentAddressString}
            suggestedGeoAddresses={
              addressConfirmationModal.suggestedGeoAddresses
            }
            onSuccess={onAddressConfirmationSuccess}
            onClose={onAddressConfirmationCancel}
          />
        )}
      </Container>
      {showCongratulatoryModal && (
        <CongratulatoryModal onClose={closeCongratulatoryModal} />
      )}
    </>
  );
}
